/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Typography, theme } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Full-screen splash animation shown during the initial auth/store load.
 * Replaces the default Ant Design <Spin fullscreen />.
 *
 * Props:
 *   visible {boolean} — controls visibility; AnimatePresence handles the fade-out.
 */
function SplashScreen({ visible }) {
  const { token } = theme.useToken();

  const bounce = {
    animate: { y: [0, -52, 0] },
    transition: {
      duration: 0.65,
      repeat: Infinity,
      ease: [0.33, 0, 0.66, 1],
    },
  };

  const shadow = {
    animate: { scaleX: [1, 0.4, 1], opacity: [0.22, 0.06, 0.22] },
    transition: {
      duration: 0.65,
      repeat: Infinity,
      ease: [0.33, 0, 0.66, 1],
    },
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="splash"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.35, ease: 'easeInOut' } }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: token.colorBgLayout,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>

          {/*
            Group centered as a unit.
            translateY on the ball doesn't affect layout flow so the
            bounding box of this group stays constant — flex centering works correctly.
          */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

            {/* Bouncing ball — translateY keeps it in flow */}
            <motion.div
              animate={bounce.animate}
              transition={bounce.transition}
              style={{ fontSize: 60, lineHeight: 1, userSelect: 'none' }}>
              ⚽
            </motion.div>

            {/* Shadow — stays in place, scales with the bounce */}
            <motion.div
              animate={shadow.animate}
              transition={shadow.transition}
              style={{
                width: 46,
                height: 10,
                marginTop: 6,
                borderRadius: '50%',
                background: token.colorText,
              }}
            />

            {/* App name */}
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.4 }}
              style={{ marginTop: 28 }}>
              <Typography.Text
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: token.colorTextSecondary,
                }}>
                GameBooking
              </Typography.Text>
            </motion.div>

          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default SplashScreen;
